'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useState } from 'react';
import { type Selection } from 'react-aria-components';
import { useFieldArray } from 'react-hook-form';
import { useListData } from 'react-stately';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { type InterestsData } from '@module/mdrt-org/shared/utils/data/graphql/utils/get-topics';
import { ButtonVariant } from '@shared/ui-components/button/buttonVariant';
import { Chip } from '@shared/ui-components/chip/chip';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { TagsList } from '@shared/ui-components/tags-list/tags-list';
import { type SitePreferencesFormData } from '../../../utils/types/form-data';
import styles from './interests-section.module.scss';
type InterestsSectionProps = {
  allTopics: InterestsData;
  cmsContent: {
    addMoreTopicsButtonLabel: string;
    cancelButtonLabel: string;
    description: string;
    interestModalSubTitle: string;
    interestModalTitle: string;
    interestsErrorMessage: string;
    saveButtonLabel: string;
    title: string;
  };
};
const INTERESTS_MODAL_TITLE_ID = 'interest-modal-title';
export const InterestsSection = t0 => {
  const $ = _c(51);
  const {
    allTopics,
    cmsContent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "interests"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    append,
    fields,
    remove
  } = useFieldArray(t1);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  let fieldTopicIds;
  let t2;
  let t3;
  if ($[1] !== fields) {
    fieldTopicIds = fields.map(_temp);
    t2 = useState;
    t3 = new Set(fieldTopicIds);
    $[1] = fields;
    $[2] = fieldTopicIds;
    $[3] = t2;
    $[4] = t3;
  } else {
    fieldTopicIds = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  const [selectedKeys, setSelectedKeys] = t2(t3);
  const {
    addMoreTopicsButtonLabel,
    cancelButtonLabel,
    description,
    interestModalSubTitle,
    interestModalTitle,
    interestsErrorMessage,
    saveButtonLabel,
    title
  } = cmsContent;
  let t4;
  if ($[5] !== fields) {
    t4 = fields.map(_temp2);
    $[5] = fields;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      initialItems: t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const selectedInterests = useListData(t5);
  let t6;
  if ($[9] !== allTopics) {
    t6 = allTopics.map(_temp3);
    $[9] = allTopics;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = {
      initialItems: t6
    };
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const allInterests = useListData(t7);
  let t8;
  if ($[13] !== errorMessage) {
    t8 = newKeys => {
      const selectedSize = newKeys instanceof Set ? newKeys.size : 0;
      setSelectedKeys(newKeys);
      if (selectedSize >= 3 && errorMessage) {
        setErrorMessage(undefined);
      }
    };
    $[13] = errorMessage;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleSelectionChange = t8;
  let t9;
  if ($[15] !== allInterests || $[16] !== append || $[17] !== fields || $[18] !== interestsErrorMessage || $[19] !== remove || $[20] !== selectedInterests || $[21] !== selectedKeys) {
    t9 = () => {
      const selectedTopics = Array.from(selectedKeys);
      if (selectedTopics.length < 3) {
        setErrorMessage(interestsErrorMessage);
        return;
      }
      const newTopics = selectedTopics.filter(key => !fields.some(t10 => {
        const {
          topicId: topicId_2
        } = t10;
        return topicId_2 === key;
      })).map(key_0 => allInterests.getItem(key_0)).filter(_temp4);
      const removedFieldIndexes = fields.map((t11, index) => {
        const {
          topicId: topicId_3
        } = t11;
        return selectedTopics.includes(topicId_3) ? null : index;
      }).filter(_temp5);
      if (removedFieldIndexes) {
        remove(removedFieldIndexes);
        for (const {
          id
        } of selectedInterests.items.filter(item_0 => !selectedTopics.includes(item_0.id))) {
          selectedInterests.remove(id);
        }
      }
      append(newTopics.map(_temp6));
      selectedInterests.append(...newTopics);
      setIsOpen(false);
    };
    $[15] = allInterests;
    $[16] = append;
    $[17] = fields;
    $[18] = interestsErrorMessage;
    $[19] = remove;
    $[20] = selectedInterests;
    $[21] = selectedKeys;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const saveInterests = t9;
  let t10;
  if ($[23] !== errorMessage || $[24] !== fieldTopicIds) {
    t10 = () => {
      if (errorMessage) {
        setErrorMessage(undefined);
      }
      setSelectedKeys(new Set(fieldTopicIds));
      setIsOpen(false);
    };
    $[23] = errorMessage;
    $[24] = fieldTopicIds;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const cancelSelection = t10;
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setIsOpen(true);
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== addMoreTopicsButtonLabel) {
    t12 = <Chip label={addMoreTopicsButtonLabel} onClick={t11} theme={ChipTheme.Emphasis} />;
    $[27] = addMoreTopicsButtonLabel;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== description || $[30] !== selectedInterests.items || $[31] !== t12 || $[32] !== title) {
    t13 = <TagsList additionalContentSlot={t12} description={description} items={selectedInterests.items} label={title} />;
    $[29] = description;
    $[30] = selectedInterests.items;
    $[31] = t12;
    $[32] = title;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== allInterests.items || $[35] !== cancelButtonLabel || $[36] !== cancelSelection || $[37] !== errorMessage || $[38] !== handleSelectionChange || $[39] !== interestModalSubTitle || $[40] !== interestModalTitle || $[41] !== saveButtonLabel || $[42] !== saveInterests || $[43] !== selectedKeys) {
    t14 = () => <Dialog footer={<><div className={styles["interests-modal-controls"]}><Button onClick={saveInterests}>{saveButtonLabel}</Button><Button onClick={cancelSelection} variant={ButtonVariant.Secondary}>{cancelButtonLabel}</Button></div><div className={classNames(styles["interests-modal-controls"], styles["interests-modal-controls--secondary-layout"])}><Button onClick={cancelSelection} variant={ButtonVariant.Secondary}>{cancelButtonLabel}</Button><Button onClick={saveInterests}>{saveButtonLabel}</Button></div></>} footerClassName={styles["interests-modal-footer"]} hasCloseButton={false} headerClassName={styles["interests-modal-header"]} subtitle={interestModalSubTitle} title={interestModalTitle} titleId={INTERESTS_MODAL_TITLE_ID}><div className={styles["interests-modal-content"]}><TagsList aria-labelledby={INTERESTS_MODAL_TITLE_ID} centerContent={true} errorMessage={errorMessage} items={allInterests.items} onSelectionChange={handleSelectionChange} selectedKeys={selectedKeys} selectionMode="multiple" /></div></Dialog>;
    $[34] = allInterests.items;
    $[35] = cancelButtonLabel;
    $[36] = cancelSelection;
    $[37] = errorMessage;
    $[38] = handleSelectionChange;
    $[39] = interestModalSubTitle;
    $[40] = interestModalTitle;
    $[41] = saveButtonLabel;
    $[42] = saveInterests;
    $[43] = selectedKeys;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== isOpen || $[46] !== t14) {
    t15 = <ModalTrigger isOpen={isOpen}>{t14}</ModalTrigger>;
    $[45] = isOpen;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t13 || $[49] !== t15) {
    t16 = <div>{t13}{t15}</div>;
    $[48] = t13;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
};
function _temp(t0) {
  const {
    topicId
  } = t0;
  return topicId;
}
function _temp2(t0) {
  const {
    name,
    topicId: topicId_0
  } = t0;
  return {
    id: topicId_0,
    name
  };
}
function _temp3(t0) {
  const {
    name: name_0,
    topicId: topicId_1
  } = t0;
  return {
    id: topicId_1,
    name: name_0
  };
}
function _temp4(item) {
  return item !== undefined;
}
function _temp5(index_0) {
  return index_0 !== null;
}
function _temp6(t0) {
  const {
    id: id_0,
    name: name_1
  } = t0;
  return {
    name: name_1,
    topicId: id_0
  };
}